// smartreader-frontend/src/alerts/AlertContext.js
import { createContext, useEffect, useState } from 'react';
import { getRecentAlerts } from '../api/alarms';
import { connectAlertsSocket } from '../services/alerts-socket';
import { normalizeAlert } from './normalize-alert';

export const AlertContext = createContext({
  alerts: [],
  unreadCount: 0,
  isLoading: false,
  isAlertsModalOpen: false,
  openAlertsModal: () => {},
  closeAlertsModal: () => {},
  resetUnreadCount: () => {},
  error: null,
});

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const { promise, cancel } = getRecentAlerts();

    promise
      .then((data) => {
        if (!cancelled) {
          const items = Array.isArray(data) ? data : data?.items || [];
          const normalizedItems = items.map((item) => normalizeAlert(item)).filter(Boolean);
          setAlerts(normalizedItems);
          setError(null);
        }
      })
      .catch((error) => {
        if (!cancelled && error.name !== 'AbortError') {
          setError('Failed to load alerts');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      cancel();
    };
  }, []);

  useEffect(() => {
    const socket = connectAlertsSocket((alert) => {
      const normalizedAlert = normalizeAlert(alert);
      if (normalizedAlert) {
        setAlerts((prev) => [normalizedAlert, ...prev]);
        setUnreadCount((currentCount) => currentCount + 1);
      }
      setError(null);
    });

    if (!socket) return undefined;

    const handleConnect = () => setError(null);
    const handleConnectError = () => setError('Failed to connect to alerts');
    const handleDisconnect = (reason) => {
      if (reason !== 'io client disconnect') {
        setError('Alerts connection lost');
      }
    };

    socket.on('connect', handleConnect);
    socket.on('connect_error', handleConnectError);
    socket.on('disconnect', handleDisconnect);

    return () => {
      socket.off('alert');
      socket.off('connect', handleConnect);
      socket.off('connect_error', handleConnectError);
      socket.off('disconnect', handleDisconnect);
      socket.disconnect();
    };
  }, []);

  const resetUnreadCount = () => setUnreadCount(0);
  const openAlertsModal = () => {
    setIsAlertsModalOpen(true);
    resetUnreadCount();
  };
  const closeAlertsModal = () => setIsAlertsModalOpen(false);

  return (
    <AlertContext.Provider
      value={{
        alerts,
        unreadCount,
        isLoading,
        isAlertsModalOpen,
        openAlertsModal,
        closeAlertsModal,
        resetUnreadCount,
        error,
      }}
    >
      {children}
    </AlertContext.Provider>
  );
};

export default AlertContext;
